import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Box, Link, Typography, useTheme } from '@mui/material'
import request from 'api'
import { getConsentApi } from 'pages/policies/api'
import { Consent, VIEW_TYPE, buildPolicySearch } from 'pages/policies/utils'
import Detail from 'pages/policies/views/detail'
import List from 'pages/policies/views/list'
import { convertCountryCode2to3, countryCode } from 'utils/i18n'

import { useDateTime, useMappedTranslation } from '../../../../../hooks'
import legacyServicepps from '../assets/legacy-kor-servicepps.json'

export const Services = () => {
  const { versionId, appId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const dateTime = useDateTime()
  const t = useMappedTranslation()
  const view_type = versionId ? VIEW_TYPE.DETAIL : VIEW_TYPE.LIST
  const { state } = location
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const policySearch = buildPolicySearch(searchParams)
  const effectiveCountryCode = paramCountryCode ? convertCountryCode2to3(paramCountryCode) : countryCode

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [consents, setConsents] = useState<Consent[]>([])
  const [oldConsents, setOldConsents] = useState<Consent[]>([])
  const [targetConsent, setTargetConsent] = useState<Consent>()
  const [detail, setDetail] = useState<string>('<div/>')
  const currentVersion = location.pathname.includes('legacies') ? 'OLD' : 'NEW'

  const getConsent = () => {
    if (view_type === VIEW_TYPE.DETAIL && state?.consent !== undefined) {
      setTargetConsent(state.consent)
      return Promise.resolve([state.consent])
    }
    const legacyConsents = legacyServicepps.filter((item) => item.appKey === appId).sort((a, b) => b.publish - a.publish)
    setOldConsents(legacyConsents)

    if (currentVersion === 'OLD') {
      if (view_type === VIEW_TYPE.DETAIL) {
        const target = legacyConsents.find((item) => item.version === versionId)
        setTargetConsent(target)
        return Promise.resolve([target])
      }
      setConsents(legacyConsents)
      return Promise.resolve(legacyConsents)
    }

    return getConsentApi('PDP', false, '/v1/consent/registered', true, appId)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        } else {
          const data = res.data as Consent[]
          setConsents(data)
          return data
        }
      })
      .then((data) => {
        if (view_type === VIEW_TYPE.DETAIL) {
          const target = data.find((item) => item.version === versionId)
          setTargetConsent(target)
          return [target]
        }
      })
  }

  const handleButton = (item) => {
    if (item === 'old') {
      setIsLoading(true)
      navigate(`/policies/privacy-notices/services/legacies/${appId}${policySearch}`)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else if (item === 'new') {
      setIsLoading(true)
      navigate(`/policies/privacy-notices/services/${appId}${policySearch}`)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      if (currentVersion === 'OLD') {
        return navigate(`/policies/privacy-notices/services/legacies/${item.appKey}/${item.version}${policySearch}`, {
          state: { consent: item }
        })
      } else {
        return navigate(`/policies/privacy-notices/services/${item.appKey}/${item.version}${policySearch}`, {
          state: { consent: item }
        })
      }
    }
  }
  const renderMinorButton = () => {
    return (
      <Link
        sx={{
          textDecoration: 'none',
          fontSize: '15px',
          lineHeight: '24px',
          fontWeight: '700',
          color: '#0381FE'
        }}
        to={location.pathname.substring(0, location.pathname.lastIndexOf('/')) + policySearch}
        component={RouterLink}
        data-testid='Link4035A62D'
      >
        {t('iam.01.Common.Notice.detail.list.01')}
      </Link>
    )
  }

  const renderAction = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          pt: { xs: '52px', md: '72px' }
        }}
      >
        <Typography
          className='off'
          fontSize='13px'
          lineHeight='16px'
          fontWeight='700'
          color={theme.palette.mode === 'dark' ? '#999999' : '#636363'}
        >
          {t('iam.01.Common.Txt.Effective.date', { 0: dateTime(Number(state?.consent?.publish), { format: 'withoutTime' }) })}
        </Typography>
      </Box>
    )
  }

  useEffect(() => {
    setIsLoading(true)
    setDetail('<div/>')
    if (effectiveCountryCode !== 'KOR') {
      navigate(`/error/404?url=${location.pathname}`, { replace: true })
    }

    getConsent()
      .then((data) => {
        if (view_type === VIEW_TYPE.DETAIL) {
          return request.get(data[0].uri).then((res) => {
            if (res.status !== 200) {
              throw new Error('Failed getConsentBody')
            }

            const document = new DOMParser().parseFromString(res.data, 'text/html')
            const body = document.getElementsByTagName('body').item(0)?.innerHTML
            if (body !== undefined) {
              setDetail(body)
            }
          })
        }
      })
      .catch(() => {
        setConsents([])
        setTargetConsent(undefined)
        setDetail('<div>Failed to load</div>')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [appId, currentVersion, versionId, effectiveCountryCode, paramCountryCode, paramLocale])

  return (
    <Box>
      {view_type === VIEW_TYPE.LIST ? (
        <List
          title={consents[0]?.title || ''}
          consents={consents}
          isLoading={isLoading}
          handleListButton={handleButton}
          isPublishTitle
          showOldButton={currentVersion === 'NEW' && oldConsents.length > 0}
          showNewButton={currentVersion === 'OLD' && oldConsents.length > 0}
        />
      ) : (
        <Detail
          title={(targetConsent && targetConsent.title) || ''}
          isLoading={isLoading}
          minorButton={renderMinorButton()}
          detail={detail}
          action={renderAction()}
        />
      )}
    </Box>
  )
}
